import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import { BsFileTextFill } from "react-icons/bs";
import { FaBriefcase, FaSave } from "react-icons/fa";
import { HiUserGroup } from "react-icons/hi";
import { IoChevronDown, IoChevronUp, IoText } from "react-icons/io5";
import { MdDelete } from "react-icons/md";

import { PREDEFINED_USE_CASES, STORAGE_KEYS } from "../../../config/constants";
import styles from "./MethodSelection.module.css";

const SAVED_CUSTOM_USE_CASES_KEY = STORAGE_KEYS.CUSTOM_USE_CASES;

/** Load saved custom use cases from localStorage */
const loadSavedUseCases = () => {
  try {
    const saved = localStorage.getItem(SAVED_CUSTOM_USE_CASES_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

/** Save custom use cases to localStorage */
const persistUseCases = (useCases) => {
  localStorage.setItem(SAVED_CUSTOM_USE_CASES_KEY, JSON.stringify(useCases));
};

const MethodSelection = ({
  selectedMethod,
  setSelectedMethod,
  useCase,
  setUseCase,
  workTitle,
  setWorkTitle,
  intendedAudience,
  setIntendedAudience,
  summaryStyle,
  setSummaryStyle,

  selectedModel,
  temperature,
  setTemperature,
  currentStep = 1,
  onContinue
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [savedCustomUseCases, setSavedCustomUseCases] = useState(loadSavedUseCases);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const isActive = currentStep === 1;
  const isCompleted = currentStep > 1;
  const isDisabled = currentStep < 1;

  const allUseCaseOptions = [...PREDEFINED_USE_CASES, ...savedCustomUseCases.map(c => c.name)];
  const isCustomUseCase = isCustomMode || (!allUseCaseOptions.includes(useCase) && useCase !== "");

  // Keep custom mode in sync: if user selects a predefined option, exit custom mode
  useEffect(() => {
    if (allUseCaseOptions.includes(useCase)) {
      setIsCustomMode(false);
    }
  }, [useCase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUseCaseChange = (e) => {
    const value = e.target.value;
    if (value === "custom") {
      setIsCustomMode(true);
      setUseCase(""); // Clear to allow typing
    } else {
      setIsCustomMode(false);
      setUseCase(value);
    }
  };

  const handleSaveCustomUseCase = () => {
    if (!useCase.trim()) return;
    const trimmed = useCase.trim();
    // Don't save if already predefined or already saved
    if (PREDEFINED_USE_CASES.includes(trimmed)) return;
    if (savedCustomUseCases.some(c => c.name === trimmed)) return;

    const newEntry = { name: trimmed, savedAt: new Date().toISOString() };
    const updated = [...savedCustomUseCases, newEntry];
    setSavedCustomUseCases(updated);
    persistUseCases(updated);
    setIsCustomMode(false);
  };

  const handleDeleteCustomUseCase = (name) => {
    const updated = savedCustomUseCases.filter(c => c.name !== name);
    setSavedCustomUseCases(updated);
    persistUseCases(updated);
    // If the deleted one was selected, reset to first predefined
    if (useCase === name) {
      setUseCase(PREDEFINED_USE_CASES[0]);
    }
  };

  return (
    <div className={`${styles.methodSelection} ${isActive ? styles.active : ''
      } ${isCompleted ? styles.completed : ''} ${isDisabled ? styles.disabled : ''
      }`}>
      <div className={styles.sectionHeader}>
        <h2>
          Step 1: Configure
          {isCompleted && <span className={styles.completedBadge}></span>}
        </h2>
        <button
          className={styles.collapseBtn}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? "Collapse section" : "Expand section"}
          disabled={isDisabled}
        >
          {isExpanded ? <IoChevronUp /> : <IoChevronDown />}
        </button>
      </div>

      {isExpanded && (isActive || isCompleted) && (
        <div className={styles.settingsGrid}>
          <div className={styles.settingCard}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>
                <IoText />
              </span>
              <h3>Work Title</h3>
            </div>
            <input
              type="text"
              value={workTitle}
              onChange={(e) => setWorkTitle(e.target.value)}
              placeholder="e.g. My Research Paper"
              className={styles.cardInput}
            />
            <p className={styles.helpText}>Optional identifier for history</p>
          </div>

          <div className={styles.settingCard}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>
                <FaBriefcase />
              </span>
              <h3>Use Case</h3>
            </div>
            <select
              value={isCustomUseCase ? "custom" : useCase}
              onChange={handleUseCaseChange}
              className={styles.cardSelect}
            >
              {PREDEFINED_USE_CASES.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
              {savedCustomUseCases.length > 0 && (
                <optgroup label="Saved Custom">
                  {savedCustomUseCases.map(({ name }) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </optgroup>
              )}
              <option value="custom">Other (Custom)</option>
            </select>

            {isCustomUseCase && (
              <div className={styles.customUseCaseRow}>
                <input
                  type="text"
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value)}
                  placeholder="Describe your use case (e.g., Podcast Script, Press Release)..."
                  className={styles.cardInput}
                />
                {useCase.trim() && !savedCustomUseCases.some(c => c.name === useCase.trim()) && (
                  <button
                    className={styles.saveUseCaseBtn}
                    onClick={handleSaveCustomUseCase}
                    title="Save this use case for future use"
                    aria-label="Save custom use case"
                  >
                    <FaSave />
                  </button>
                )}
              </div>
            )}

            {/* Show delete button for saved custom use cases */}
            {!isCustomUseCase && savedCustomUseCases.some(c => c.name === useCase) && (
              <button
                className={styles.deleteUseCaseBtn}
                onClick={() => handleDeleteCustomUseCase(useCase)}
                title="Remove this saved use case"
                aria-label="Delete custom use case"
              >
                <MdDelete className={styles.deleteIcon} /> Remove
              </button>
            )}

            <p className={styles.helpText}>
              {isCustomUseCase
                ? "Custom use cases are auto-enhanced into tailored prompts"
                : "Predefined use case with optimized prompt"}
            </p>
          </div>

          <div className={styles.settingCard}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>
                <HiUserGroup />
              </span>
              <h3>Audience</h3>
            </div>
            <select
              value={intendedAudience}
              onChange={(e) => setIntendedAudience(e.target.value)}
              className={styles.cardSelect}
            >
              <option value="general">General Public</option>
              <option value="students">Students</option>
              <option value="experts">Experts</option>
            </select>
            <p className={styles.helpText}>Tailor language complexity</p>
          </div>

          <div className={styles.settingCard}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>
                <BsFileTextFill />
              </span>
              <h3>Style</h3>
            </div>
            <select
              value={summaryStyle}
              onChange={(e) => setSummaryStyle(e.target.value)}
              className={styles.cardSelect}
            >
              <option value="concise">Concise</option>
              <option value="detailed">Detailed</option>
              <option value="bullet_points">Bullet Points</option>
            </select>
            <p className={styles.helpText}>Output format preference</p>
          </div>


        </div>
      )}

      {isActive && isExpanded && (
        <div className={styles.stepActions}>
          <button
            className={styles.continueBtn}
            onClick={onContinue}
            aria-label="Continue to Upload"
          >
            Continue to Upload →
          </button>
        </div>
      )}
    </div>
  );
};

MethodSelection.propTypes = {
  selectedMethod: PropTypes.string.isRequired,
  setSelectedMethod: PropTypes.func.isRequired,
  useCase: PropTypes.string,
  setUseCase: PropTypes.func,
  workTitle: PropTypes.string,
  setWorkTitle: PropTypes.func,
  intendedAudience: PropTypes.string.isRequired,
  setIntendedAudience: PropTypes.func.isRequired,
  summaryStyle: PropTypes.string.isRequired,
  setSummaryStyle: PropTypes.func.isRequired,

  selectedModel: PropTypes.string.isRequired,
  temperature: PropTypes.number.isRequired,
  setTemperature: PropTypes.func.isRequired,
  currentStep: PropTypes.number,
  onContinue: PropTypes.func,
};

export default MethodSelection;